import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api, ApiError, setLinkTokenProvider } from '../api';
import {
  EVALUATION_TYPE_PARAMS,
  evaluationTypeFromSnapshot,
  masteryThresholdFromSnapshot,
} from '@marzano/contracts';
import type {
  ChatTurn,
  Dictamen,
  Dim,
  HelpType,
  MarzanoMatrix,
  PilotInput,
  SessionRecord,
} from '../types';
import { InterviewPanel, type GrantedHelpItem } from './InterviewPanel';
import {
  buildGrantedHelpEntry,
  buildHelpChatUpdate,
  buildTurnChatUpdate,
} from '../hooks/interviewReducers';
import { LearnerWelcome } from './learner/LearnerWelcome';
import { LearnerError, LearnerLoading } from './learner/LearnerEntryStates';
import { ReportViewer } from './ReportViewer';

// Persistimos el token en sessionStorage (no localStorage): si el alumno cierra
// la pestaña, un tercero que abra la misma ventana desde el historial NO
// hereda la sesión. Mantiene ergonomía si recarga la página en la misma pestaña.
const STORAGE_KEY = 'marzano.link_token';

interface LinkPayload {
  pilot_id: string;
  classroom_id: string;
  learner_email: string;
  site_id: string;
  exp: number;
}

/**
 * Decodifica el payload del JWT SIN verificar firma — la verificación real es
 * responsabilidad del backend cuando llega el header X-Link-Token. Acá solo
 * necesitamos leer `pilot_id` para armar las requests siguientes.
 */
function decodePayload(token: string): LinkPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    const json = atob(b64 + '='.repeat(pad));
    const parsed = JSON.parse(json) as Partial<LinkPayload>;
    if (
      typeof parsed.pilot_id === 'string' &&
      typeof parsed.classroom_id === 'string' &&
      typeof parsed.learner_email === 'string' &&
      typeof parsed.site_id === 'string' &&
      typeof parsed.exp === 'number'
    ) {
      return parsed as LinkPayload;
    }
    return null;
  } catch {
    return null;
  }
}

interface Props {
  /** Token crudo del path `/entrar/:token`. */
  rawToken: string;
}

type Phase =
  | { kind: 'validating' }
  | { kind: 'error'; message: string }
  | { kind: 'ready' }
  | { kind: 'starting' }
  | { kind: 'interview' }
  | { kind: 'done'; dictamen: Dictamen };

interface PilotState {
  matrix: MarzanoMatrix;
  session: SessionRecord;
  chatHistory: ChatTurn[];
  currentDim: Dim | null;
  evidenceCounts: Record<Dim, number>;
  input: PilotInput;
  grantedHelps: GrantedHelpItem[];
}

export function LearnerEntry({ rawToken }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'validating' });
  const [payload, setPayload] = useState<LinkPayload | null>(null);
  const [pilot, setPilot] = useState<PilotState | null>(null);

  // Registra el link-token globalmente ANTES de cualquier request. El
  // provider queda vigente hasta que el componente se desmonta o el token
  // cambia (ej. profe re-emite el invite).
  useEffect(() => {
    const decoded = decodePayload(rawToken);
    if (!decoded) {
      setPhase({ kind: 'error', message: 'El link es inválido o está corrupto.' });
      return;
    }
    if (decoded.exp * 1000 < Date.now()) {
      setPhase({
        kind: 'error',
        message: 'El link venció. Pedile a tu profesora que te envíe uno nuevo.',
      });
      return;
    }
    setPayload(decoded);
    setLinkTokenProvider(() => rawToken);
    sessionStorage.setItem(STORAGE_KEY, rawToken);
    return () => setLinkTokenProvider(null);
  }, [rawToken]);

  // Fetch inicial del pilot: contexto (OA, hito, matriz) para la vista de
  // bienvenida y para el InterviewPanel posterior.
  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await api.getPilot(payload.pilot_id, payload.site_id);
        if (cancelled) return;
        const oa = state.session.oa_snapshot as {
          oa_codigo?: string;
          oa_descripcion?: string;
          asignatura?: string;
          nivel_academico?: string;
          tipo?: PilotInput['oa_tipo'];
          matrix?: MarzanoMatrix;
          oas?: Array<{ oa_codigo: string; oa_descripcion: string; tipo?: PilotInput['oa_tipo'] }>;
          student_name?: string;
        };
        const hito = state.session.milestone_snapshot as {
          hito_nombre?: string;
          hito_descripcion?: string;
        };
        if (!oa.matrix) {
          setPhase({ kind: 'error', message: 'La evaluación todavía no tiene contenido listo.' });
          return;
        }
        const reconstructed: PilotInput = {
          site_id: payload.site_id,
          // Preferimos el nombre persistido en el snapshot (creación reciente).
          // Fallback al email para pilots viejos que no lo tienen.
          student_name: oa.student_name ?? payload.learner_email,
          asignatura: oa.asignatura ?? '',
          nivel_academico: oa.nivel_academico ?? '',
          oa_codigo: oa.oa_codigo ?? '',
          oa_descripcion: oa.oa_descripcion ?? '',
          hito_nombre: hito.hito_nombre ?? '',
          hito_descripcion: hito.hito_descripcion ?? '',
          modality: state.session.modality,
          oa_tipo: oa.tipo ?? 'conceptual',
          ...(oa.oas && oa.oas.length > 0 && { oas: oa.oas }),
        };
        setPilot({
          matrix: oa.matrix,
          session: state.session,
          chatHistory: state.recent_turns,
          currentDim: state.session.current_dim,
          evidenceCounts: state.session.evidence_counts,
          input: reconstructed,
          grantedHelps: [],
        });
        setPhase(state.dictamen ? { kind: 'done', dictamen: state.dictamen } : { kind: 'ready' });
      } catch (err) {
        setPhase({ kind: 'error', message: formatEntryError(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (phase.kind === 'validating') {
    return <LearnerLoading title="Verificando link…" subtitle="Estamos preparando tu evaluación." />;
  }

  if (phase.kind === 'error') {
    return <LearnerError message={phase.message} />;
  }

  if (phase.kind === 'ready' && pilot) {
    // Vista simple para el alumno — la rúbrica Marzano (matrix, pesos,
    // criterios, banco de preguntas) NO se muestra al alumno: es abrumador y
    // le adelanta qué esperan de él. Esa vista queda solo para el docente.
    // El rango sale del tipo de actividad; las sesiones previas (sin tipo en el
    // snapshot) muestran su presupuesto real.
    const tipoActividad = evaluationTypeFromSnapshot(pilot.session.milestone_snapshot);
    const tiempoEstimado = tipoActividad
      ? EVALUATION_TYPE_PARAMS[tipoActividad].expectedMinutesLabel
      : `${Math.round(pilot.session.initial_budget_sec / 60)} min`;
    const oasCount = pilot.input.oas?.length ?? 1;
    return (
      <LearnerWelcome
        studentName={pilot.input.student_name}
        asignatura={pilot.input.asignatura}
        oasCount={oasCount}
        modality={pilot.input.modality}
        tiempoEstimado={tiempoEstimado}
        onStart={async () => {
          if (!payload) return;
          setPhase({ kind: 'starting' });
          try {
            const resp = await api.startInterview(payload.pilot_id, payload.site_id);
            const state = await api.getPilot(payload.pilot_id, payload.site_id);
            setPilot({
              ...pilot,
              session: state.session,
              chatHistory: state.recent_turns,
              currentDim: (resp.current_dim as Dim) ?? 'recuperacion',
              evidenceCounts: state.session.evidence_counts,
            });
            setPhase({ kind: 'interview' });
          } catch (err) {
            setPhase({ kind: 'error', message: formatEntryError(err) });
          }
        }}
      />
    );
  }

  if (phase.kind === 'starting') {
    return <LearnerLoading title="Cargando la primera pregunta…" />;
  }

  if (phase.kind === 'interview' && pilot && payload) {
    return (
      <InterviewPanel
        sessionId={payload.pilot_id}
        siteId={payload.site_id}
        initialBudgetSec={pilot.session.initial_budget_sec}
        oralSecondsUsedInitial={pilot.session.oral_seconds_used}
        textSecondsUsedInitial={pilot.session.text_seconds_used}
        modality={pilot.input.modality}
        studentName={pilot.input.student_name}
        oaCodigo={pilot.input.oa_codigo}
        hitoNombre={pilot.input.hito_nombre}
        chatHistory={pilot.chatHistory}
        currentDim={pilot.currentDim}
        evidenceCounts={pilot.evidenceCounts}
        isAwaitingFirst={pilot.chatHistory.length === 0}
        isBusy={false}
        variant="learner"
        topicLabel={pilot.input.asignatura}
        onSubmitText={async (text, seconds, metadata) => {
          try {
            const resp = await api.submitTurnText(payload.pilot_id, payload.site_id, text, seconds, metadata);
            const learnerTurn: ChatTurn = {
              role: 'learner',
              text,
              observed_at: new Date().toISOString(),
              modality: 'written',
            };
            setPilot((p) =>
              p
                ? {
                    ...p,
                    chatHistory: buildTurnChatUpdate(p.chatHistory, resp, learnerTurn),
                    currentDim: resp.current_dim,
                    evidenceCounts: resp.evidence_counts,
                  }
                : p,
            );
            if (resp.closed && resp.dictamen) {
              setPhase({ kind: 'done', dictamen: resp.dictamen });
            }
            return resp;
          } catch (err) {
            alert(formatEntryError(err));
            return undefined;
          }
        }}
        onSubmitAudio={async (blob, duration) => {
          try {
            const resp = await api.submitTurnAudio(payload.pilot_id, payload.site_id, blob, duration);
            const learnerTurn: ChatTurn = {
              role: 'learner',
              text: resp.transcript ?? '(audio sin transcripción)',
              observed_at: new Date().toISOString(),
              modality: 'oral',
            };
            setPilot((p) =>
              p
                ? {
                    ...p,
                    chatHistory: buildTurnChatUpdate(p.chatHistory, resp, learnerTurn),
                    currentDim: resp.current_dim,
                    evidenceCounts: resp.evidence_counts,
                  }
                : p,
            );
            if (resp.closed && resp.dictamen) {
              setPhase({ kind: 'done', dictamen: resp.dictamen });
            }
            return resp;
          } catch (err) {
            alert(formatEntryError(err));
            return undefined;
          }
        }}
        onGrantHelp={async (helpType: HelpType) => {
          try {
            const resp = await api.registerHelp(payload.pilot_id, payload.site_id, helpType);
            setPilot((p) =>
              p
                ? {
                    ...p,
                    chatHistory: buildHelpChatUpdate(p.chatHistory, resp, helpType),
                    grantedHelps: [
                      ...p.grantedHelps,
                      buildGrantedHelpEntry(resp, helpType, p.currentDim),
                    ],
                  }
                : p,
            );
          } catch (err) {
            alert(formatEntryError(err));
          }
        }}
        grantedHelps={pilot.grantedHelps ?? []}
        onCloseEarly={async () => {
          try {
            const { dictamen } = await api.closePilot(
              payload.pilot_id,
              '',
              'teacher_early_close',
            );
            setPhase({ kind: 'done', dictamen });
          } catch (err) {
            alert(formatEntryError(err));
          }
        }}
        onTimeExhausted={async () => {
          try {
            const { dictamen } = await api.closePilot(payload.pilot_id, payload.site_id, 'time_exhausted');
            setPhase({ kind: 'done', dictamen });
          } catch (err) {
            alert(formatEntryError(err));
          }
        }}
      />
    );
  }

  if (phase.kind === 'done' && pilot && payload) {
    return (
      <ReportViewer
        dictamen={phase.dictamen}
        chatHistory={pilot.chatHistory}
        studentName={pilot.input.student_name}
        oaCodigo={pilot.input.oa_codigo}
        hitoNombre={pilot.input.hito_nombre}
        sessionId={payload.pilot_id}
        siteId={payload.site_id}
        viewerRole="learner"
        masteryThreshold={masteryThresholdFromSnapshot(pilot.session.milestone_snapshot)}
        onBackToList={() => {
          sessionStorage.removeItem(STORAGE_KEY);
          window.location.href = '/';
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );
}

function formatEntryError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'LINK_TOKEN_EXPIRED') return 'El link venció. Pedile a tu profesora uno nuevo.';
    if (err.code === 'LINK_TOKEN_REVOKED') return 'La profesora cerró el aula o revocó el acceso.';
    if (err.code === 'LINK_TOKEN_INVALID') return 'El link no corresponde a esta evaluación.';
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}
